import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Power, ShieldAlert, PauseCircle, PlayCircle, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const money = (v) => (v == null ? "—" : v < 0 ? "∞" : `$${Number(v).toFixed(2)}`);

function Bar({ used, cap }) {
  const unlimited = cap == null || cap < 0;
  const pct = unlimited || !cap ? 0 : Math.min(100, (used / cap) * 100);
  const danger = pct >= 90;
  return (
    <div className="h-2 rounded-full bg-muted/40 overflow-hidden mt-1">
      {!unlimited && <div className={`h-full ${danger ? "bg-red-500" : "bg-signal-500"}`} style={{ width: `${pct}%` }} />}
    </div>
  );
}

export default function MobileBudget() {
  const { reloadBadges } = useOutletContext() || {};
  const [b, setB] = useState(null);
  const [kill, setKill] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [busy, setBusy] = useState(false);
  const [caps, setCaps] = useState({ daily_usd: "", monthly_usd: "", hard_stop_enabled: false });

  const load = async () => {
    const [bd, ks, og] = await Promise.all([
      api.get("/budget").catch(() => ({ data: null })),
      api.get("/system/kill-switch").catch(() => ({ data: null })),
      api.get("/system/outreach").catch(() => ({ data: null })),
    ]);
    setB(bd.data); setKill(ks.data); setOutreach(og.data);
    if (bd.data?.caps) {
      setCaps({
        daily_usd: bd.data.caps.daily_usd ?? "",
        monthly_usd: bd.data.caps.monthly_usd ?? "",
        hard_stop_enabled: !!bd.data.caps.hard_stop_enabled,
      });
    }
  };
  useEffect(() => { load(); }, []);

  const toggleKill = async () => {
    const next = !kill?.active;
    if (next && !window.confirm("Pause ALL AI usage now? This stops the sequencer, page engagement, AI drafts and every LLM call until you turn it back on.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/system/kill-switch", { active: next, reason: next ? "Manual stop from mobile" : "" });
      setKill(data);
      toast[next ? "warning" : "success"](next ? "AI fully paused" : "AI resumed");
      reloadBadges?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Only the owner can do this"); }
    setBusy(false);
  };

  const toggleOutreach = async () => {
    const next = !outreach?.active;
    setBusy(true);
    try {
      const { data } = await api.post("/system/outreach", { active: next });
      setOutreach(data);
      toast.success(next ? "Outreach sending resumed" : "Outreach sending paused");
    } catch (e) { toast.error(e?.response?.data?.detail || "Only the owner can do this"); }
    setBusy(false);
  };

  const saveCaps = async () => {
    setBusy(true);
    try {
      await api.put("/budget", {
        daily_usd: caps.daily_usd === "" ? null : Number(caps.daily_usd),
        monthly_usd: caps.monthly_usd === "" ? null : Number(caps.monthly_usd),
        hard_stop_enabled: caps.hard_stop_enabled,
      });
      toast.success("Budget limits saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  if (!b) return <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>;

  const today = b.burn?.today || {};
  const month = b.burn?.month || {};
  const killOn = !!kill?.active;
  const outreachOn = outreach?.active !== false;

  return (
    <div className="space-y-5" data-testid="m-budget">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">AI budget & control</h1>
        <button onClick={load} className="text-muted-foreground" data-testid="m-budget-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* KILL SWITCH — hero */}
      <button onClick={toggleKill} disabled={busy}
        className={`w-full rounded-2xl p-5 text-left transition-colors border ${killOn ? "bg-red-500/15 border-red-500/50" : "bg-card border-border"}`}
        data-testid="m-kill-switch">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Power className={`w-5 h-5 ${killOn ? "text-red-400" : "text-signal-500"}`} />
            <span className="font-semibold">{killOn ? "AI is PAUSED" : "AI is running"}</span>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${killOn ? "bg-red-500 text-white" : "bg-signal-500 text-background"}`}>
            {killOn ? "Resume" : "STOP ALL"}
          </span>
        </div>
        <p className="text-[0.72rem] text-muted-foreground mt-2">
          {killOn
            ? "Every LLM call, the sequencer, page engagement and AI drafting are blocked. Tap to resume."
            : "Emergency stop — instantly halts all AI spend (sequencer, engagement, drafts, everything) if costs run away."}
        </p>
      </button>

      {/* Spend */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-4" data-testid="m-budget-spend">
        <div>
          <div className="flex items-end justify-between">
            <span className="text-xs text-muted-foreground">Today</span>
            <span className="text-sm font-semibold tabular-nums">{money(today.usd || 0)} <span className="text-muted-foreground font-normal">/ {money(b.caps?.daily_usd)}</span></span>
          </div>
          <Bar used={today.usd || 0} cap={b.caps?.daily_usd} />
          <div className="text-[0.65rem] text-muted-foreground mt-1">{today.runs || 0} runs · {(today.tokens || 0).toLocaleString()} tokens</div>
        </div>
        <div>
          <div className="flex items-end justify-between">
            <span className="text-xs text-muted-foreground">This month</span>
            <span className="text-sm font-semibold tabular-nums">{money(month.usd || 0)} <span className="text-muted-foreground font-normal">/ {money(b.caps?.monthly_usd)}</span></span>
          </div>
          <Bar used={month.usd || 0} cap={b.caps?.monthly_usd} />
          <div className="text-[0.65rem] text-muted-foreground mt-1">{month.runs || 0} runs · {(month.tokens || 0).toLocaleString()} tokens</div>
        </div>
      </div>

      {/* Outreach pause (softer than kill switch) */}
      <button onClick={toggleOutreach} disabled={busy}
        className="w-full flex items-center justify-between bg-card border border-border rounded-xl p-4 text-left"
        data-testid="m-outreach-toggle">
        <div className="flex items-center gap-2">
          {outreachOn ? <PlayCircle className="w-5 h-5 text-emerald-400" /> : <PauseCircle className="w-5 h-5 text-amber-400" />}
          <div>
            <div className="text-sm font-medium">Autonomous outreach</div>
            <div className="text-[0.7rem] text-muted-foreground">{outreachOn ? "Sending is active" : "Sending paused (AI tools still work)"}</div>
          </div>
        </div>
        <span className={`text-xs font-semibold ${outreachOn ? "text-amber-400" : "text-emerald-400"}`}>{outreachOn ? "Pause" : "Resume"}</span>
      </button>

      {/* Caps editor */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="m-budget-caps">
        <div className="flex items-center gap-2 text-sm font-medium"><ShieldAlert className="w-4 h-4 text-signal-500" /> Spend limits</div>
        <label className="block">
          <span className="text-[0.7rem] text-muted-foreground">Daily cap (USD, -1 = unlimited)</span>
          <input type="number" inputMode="decimal" value={caps.daily_usd}
            onChange={(e) => setCaps((c) => ({ ...c, daily_usd: e.target.value }))}
            className="w-full mt-1 bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-signal-500"
            data-testid="m-cap-daily" />
        </label>
        <label className="block">
          <span className="text-[0.7rem] text-muted-foreground">Monthly cap (USD, -1 = unlimited)</span>
          <input type="number" inputMode="decimal" value={caps.monthly_usd}
            onChange={(e) => setCaps((c) => ({ ...c, monthly_usd: e.target.value }))}
            className="w-full mt-1 bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-signal-500"
            data-testid="m-cap-monthly" />
        </label>
        <label className="flex items-center justify-between py-1">
          <span className="text-sm">Auto-stop AI when a cap is hit</span>
          <input type="checkbox" checked={caps.hard_stop_enabled}
            onChange={(e) => setCaps((c) => ({ ...c, hard_stop_enabled: e.target.checked }))}
            className="w-5 h-5 accent-signal-500" data-testid="m-cap-hardstop" />
        </label>
        <button onClick={saveCaps} disabled={busy}
          className="w-full flex items-center justify-center gap-2 bg-signal-500 text-background font-semibold rounded-lg py-2.5 disabled:opacity-60"
          data-testid="m-cap-save">
          <Save className="w-4 h-4" /> Save limits
        </button>
      </div>
    </div>
  );
}
